import type { Table } from '@tanstack/angular-table';
import type { LabeledColumn } from '../../../components/data-table/types';

export interface TableExportAtom {
  exportCsv<T>(table: Table<T>, columns: LabeledColumn[], filename: string): void;
  exportXls<T>(table: Table<T>, columns: LabeledColumn[], filename: string): void;
}

/**
 * Atom: table export helpers. Single responsibility — generates CSV/XLS
 * blobs from the visible rows of a TanStack `Table<T>` and triggers a
 * browser download. Stateless — pure functions wrapped as an atom for
 * uniformity with the other table atoms.
 *
 * Exports the CURRENT page only. Full-dataset export is a separate concern
 * (typically requires a fetcher call, not a table operation).
 */
export function useTableExport(): TableExportAtom {
  return {
    exportCsv<T>(table: Table<T>, columns: LabeledColumn[], filename: string): void {
      const csv = generateExportCsv(table, columns);
      downloadFile(csv, `${filename}.csv`, 'text/csv');
    },
    exportXls<T>(table: Table<T>, columns: LabeledColumn[], filename: string): void {
      const csv = generateExportCsv(table, columns);
      downloadFile(csv, `${filename}.xls`, 'application/vnd.ms-excel');
    },
  };
}

/**
 * es-ES formatter for NUMERIC export cells. Mirrors the on-screen rounding
 * (`build-report-columns.ts` uses plain `Intl.NumberFormat('es-ES')`, max 3
 * fraction digits by default) but drops thousands grouping: a Spanish-locale
 * Excel reading an UNQUOTED cell needs the plain `1234,568` shape, not
 * `1.234,568`, to parse it as a number.
 */
const numericExportFormatter = new Intl.NumberFormat('es-ES', {
  maximumFractionDigits: 3,
  useGrouping: false,
});

/**
 * Formats one export cell.
 *
 * `numeric: true` (declared by the caller on `LabeledColumn` — NEVER
 * inferred from the value, so a padded code like `'00123'` stays text) is
 * written UNQUOTED so a Spanish-locale Excel parses it as a number instead
 * of text. null/undefined/empty on a numeric column is an empty cell, not
 * `"0"`. A numeric column whose value fails to parse as a finite number
 * falls back to the historical quoted-text shape below.
 *
 * Every other cell keeps the historical quoted, `"`-escaped text shape.
 */
function formatExportCell(value: unknown, numeric: boolean | undefined): string {
  if (numeric) {
    if (value === null || value === undefined || value === '') return '';
    const num = typeof value === 'number' ? value : Number(value);
    if (Number.isFinite(num)) {
      return numericExportFormatter.format(num);
    }
    // Declared numeric but not a finite number — fall back to quoted text below.
  }
  const text = String(value ?? '').replace(/"/g, '""');
  return `"${text}"`;
}

/**
 * Pure CSV/XLS body generator — the SINGLE generator shared by `useTableExport`
 * (this atom) and the `TableExport` component, previously duplicated in both
 * places. The duplication is what let the numeric-formatting fix land in only
 * one of the two paths; keep them merged here going forward.
 */
export function generateExportCsv<T>(table: Table<T>, columns: LabeledColumn[]): string {
  const rows = table.getRowModel().rows;
  const header = columns.map((c) => `"${c.label}"`).join(';');
  const body = rows.map((row) =>
    columns.map((c) => formatExportCell(row.getValue(c.key), c.numeric)).join(';'),
  );
  return [header, ...body].join('\n');
}

function downloadFile(content: string, filename: string, mimeType: string): void {
  const bom = '﻿';
  const blob = new Blob([bom + content], { type: `${mimeType};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
